/**
 * Pipeline filter & sort helpers — used by Leads, Quotations, Approvals,
 * Proforma Invoices, Customers, and My Team.
 *
 * User request (Feb 2026): sort by close-date + value, filter by "this
 * month/quarter" close-window, plus a Priority score option and a
 * "Forecast overdue" flag on leads whose expected_closing_date has slipped.
 *
 * Indian FY quarters (user preference):
 *   Q1: Apr–Jun, Q2: Jul–Sep, Q3: Oct–Dec, Q4: Jan–Mar
 */

/** Return YYYY-MM-DD in the LOCAL calendar (IST-safe — never uses UTC). */
export function toISODate(d) {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Parse a YYYY-MM-DD (or full ISO) string as a LOCAL date at midnight.
 *  Avoids the `new Date('2026-02-01')` → UTC-midnight shift that eats a day in IST. */
export function parseLocalDate(iso) {
  if (!iso) return null;
  const s = String(iso).split('T')[0];
  const parts = s.split('-');
  if (parts.length !== 3) return null;
  const [y, m, d] = parts.map(Number);
  const dt = new Date(y, m - 1, d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/** Return [startISO, endISO] for the current Indian FY quarter. */
export function indianFYQuarterRange(ref = new Date()) {
  const m = ref.getMonth(); // 0–11
  const y = ref.getFullYear();
  let startMonth; let startYear = y; let endMonth; let endYear = y;
  if (m >= 3 && m <= 5)      { startMonth = 3;  endMonth = 5;  }  // Apr–Jun
  else if (m >= 6 && m <= 8) { startMonth = 6;  endMonth = 8;  }  // Jul–Sep
  else if (m >= 9 && m <= 11){ startMonth = 9;  endMonth = 11; }  // Oct–Dec
  else                       { startMonth = 0;  endMonth = 2;  }  // Jan–Mar
  const start = new Date(startYear, startMonth, 1);
  const end = new Date(endYear, endMonth + 1, 0);              // last day of endMonth
  return [toISODate(start), toISODate(end)];
}

/** Preset close-window computations. Returns [startISO, endISO] or null=no filter. */
export function computeCloseWindow(preset, ref = new Date()) {
  const today = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
  const addDays = (n) => {
    const d = new Date(today); d.setDate(d.getDate() + n); return d;
  };
  switch (preset) {
    case 'overdue': {
      // yesterday and earlier — expected_closing_date has passed
      return [null, toISODate(addDays(-1))];
    }
    case 'this_week': {
      // Monday–Sunday of current week
      const dow = today.getDay();                // 0=Sun
      const monOffset = dow === 0 ? -6 : 1 - dow;
      const start = addDays(monOffset);
      const end = new Date(start); end.setDate(end.getDate() + 6);
      return [toISODate(start), toISODate(end)];
    }
    case 'this_month': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return [toISODate(start), toISODate(end)];
    }
    case 'this_quarter': return indianFYQuarterRange(today);
    case 'next_30':      return [toISODate(today), toISODate(addDays(30))];
    case 'next_60':      return [toISODate(today), toISODate(addDays(60))];
    case 'next_90':      return [toISODate(today), toISODate(addDays(90))];
    case 'all':
    default:             return null;
  }
}

/** True when the row's `dateKey` is inside [startISO, endISO] inclusive. */
export function withinCloseWindow(row, dateKey, window) {
  if (!window) return true;
  const [startISO, endISO] = window;
  const val = (row?.[dateKey] || '').split('T')[0];
  if (!val) return false;                        // no date → excluded from any window filter
  if (startISO && val < startISO) return false;
  if (endISO && val > endISO) return false;
  return true;
}

/** Weights per lead status — used by the priority scorer. Later stages
 *  score higher because those deals are "hotter". */
export const STAGE_WEIGHTS = {
  new: 0.30,
  contacted: 0.45,
  interested: 0.60,
  quoted: 0.80,
  quote_approved: 0.95,
  negotiation: 0.90,
  extended: 0.55,
  won: 1.0,
  completed: 1.0,
  lost: 0.0,
};

/** Priority score = value × imminence × stage-weight.
 *  Imminence peaks at "today" and falls off in both directions so both
 *  imminent AND slightly overdue leads bubble up. */
export function priorityScore(row, { valueKey = 'lead_value', dateKey = 'expected_closing_date' } = {}) {
  const value = Number(row?.[valueKey] || 0);
  if (!value) return 0;
  const stage = STAGE_WEIGHTS[row?.status] ?? 0.5;
  const dt = parseLocalDate(row?.[dateKey]);
  let imminence = 0.4;                           // no date → mild default
  if (dt) {
    const now = new Date();
    const days = Math.round((dt - now) / 86_400_000);
    // Gaussian-ish curve centred on today, width ~45 days.
    imminence = Math.exp(-Math.pow(days / 45, 2));
    // But actually-overdue slightly overweighted (RSM should call them).
    if (days < 0 && days > -30) imminence = Math.max(imminence, 0.85);
  }
  return value * imminence * stage;
}

/** True when the row is an OPEN lead (not won/completed/lost) with a
 *  past `expected_closing_date`. Used for the "forecast overdue" pill. */
export function isForecastOverdue(row, dateKey = 'expected_closing_date') {
  const closedStatuses = new Set(['won', 'completed', 'lost']);
  if (!row || closedStatuses.has(row.status)) return false;
  const dt = parseLocalDate(row[dateKey]);
  if (!dt) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return dt.getTime() < today.getTime();
}

/** Sort `list` in place-compatible fashion (returns new array). Supports a
 *  small enum of sort modes; unknown modes fall through to input order. */
export function sortRows(list, sortBy, opts = {}) {
  const {
    valueKey = 'lead_value',
    dateKey = 'expected_closing_date',
    createdKey = 'created_at',
    updatedKey = 'updated_at',
  } = opts;
  const arr = [...list];
  const num = (r, k) => Number(r?.[k] || 0);
  const day = (r, k) => (r?.[k] || '').split('T')[0] || '';
  switch (sortBy) {
    case 'value_desc': arr.sort((a, b) => num(b, valueKey) - num(a, valueKey)); break;
    case 'value_asc':  arr.sort((a, b) => num(a, valueKey) - num(b, valueKey)); break;
    case 'close_soon': {
      // Empty dates sink to the bottom; then earliest date first.
      arr.sort((a, b) => {
        const da = day(a, dateKey); const db = day(b, dateKey);
        if (!da && !db) return 0;
        if (!da) return 1; if (!db) return -1;
        return da < db ? -1 : da > db ? 1 : 0;
      });
      break;
    }
    case 'close_late': {
      arr.sort((a, b) => {
        const da = day(a, dateKey); const db = day(b, dateKey);
        if (!da && !db) return 0;
        if (!da) return 1; if (!db) return -1;
        return db < da ? -1 : db > da ? 1 : 0;
      });
      break;
    }
    case 'updated_desc': arr.sort((a, b) => (day(b, updatedKey) < day(a, updatedKey) ? -1 : 1)); break;
    case 'created_desc': arr.sort((a, b) => (day(b, createdKey) < day(a, createdKey) ? -1 : 1)); break;
    case 'priority':     arr.sort((a, b) => priorityScore(b, { valueKey, dateKey }) - priorityScore(a, { valueKey, dateKey })); break;
    default: break;
  }
  return arr;
}

/** Human labels for chip / dropdown UI. */
export const CLOSE_WINDOW_PRESETS = [
  { key: 'all',          label: 'All',            testId: 'window-all' },
  { key: 'overdue',      label: 'Forecast Overdue', testId: 'window-overdue' },
  { key: 'this_week',    label: 'This Week',      testId: 'window-week' },
  { key: 'this_month',   label: 'This Month',     testId: 'window-month' },
  { key: 'this_quarter', label: 'This Quarter (FY)', testId: 'window-quarter' },
  { key: 'next_30',      label: 'Next 30d',       testId: 'window-30' },
  { key: 'next_60',      label: 'Next 60d',       testId: 'window-60' },
  { key: 'next_90',      label: 'Next 90d',       testId: 'window-90' },
];

export const SORT_OPTIONS = [
  { key: 'created_desc', label: 'Recently created' },
  { key: 'updated_desc', label: 'Recently updated' },
  { key: 'value_desc',   label: 'Value: High → Low' },
  { key: 'value_asc',    label: 'Value: Low → High' },
  { key: 'close_soon',   label: 'Close date: Soonest first' },
  { key: 'close_late',   label: 'Close date: Latest first' },
  { key: 'priority',     label: 'Priority (blended)' },
];
